import * as Phaser from 'phaser';
import { GAME_WIDTH } from '@/config/gameConfig';
import { BALANCE } from '@/config/balance';
import { NUTRIENTS, type NutrientKey } from '@/config/nutrients';
import { GAME_EVENTS, type NutrientCollectedPayload } from '@/types';

const TOOLTIP_Y = 360;
const CARD_WIDTH = 280;
const CARD_HEIGHT = 64;
const CARD_RADIUS = 12;

export class NutrientTooltip {
  private active?: Phaser.GameObjects.Container;
  private activeTween?: Phaser.Tweens.Tween;
  private seen: Set<NutrientKey> = new Set();

  constructor(private scene: Phaser.Scene) {
    scene.events.on(GAME_EVENTS.NutrientCollected, this.onCollected, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  resetSeen(): void {
    this.seen.clear();
  }

  destroy(): void {
    this.scene.events.off(GAME_EVENTS.NutrientCollected, this.onCollected, this);
    this.active?.destroy();
    this.activeTween?.stop();
    this.active = undefined;
  }

  private onCollected(payload: NutrientCollectedPayload): void {
    if (!payload.isDielac) return;
    const key = payload.key as NutrientKey;
    if (this.seen.has(key)) return;
    this.seen.add(key);
    this.showFor(key);
  }

  private showFor(key: NutrientKey): void {
    const def = NUTRIENTS[key];
    if (!def.tooltip) return;
    this.render(def.label, def.tooltip, def.color);
  }

  private render(title: string, body: string, accentColor: number): void {
    this.active?.destroy();
    this.activeTween?.stop();

    const container = this.scene.add.container(GAME_WIDTH / 2, TOOLTIP_Y).setDepth(70);
    this.active = container;

    const bg = this.scene.add.graphics();
    bg.fillStyle(0xffffff, 0.97);
    bg.fillRoundedRect(
      -CARD_WIDTH / 2,
      -CARD_HEIGHT / 2,
      CARD_WIDTH,
      CARD_HEIGHT,
      CARD_RADIUS,
    );
    bg.lineStyle(2, accentColor, 0.95);
    bg.strokeRoundedRect(
      -CARD_WIDTH / 2,
      -CARD_HEIGHT / 2,
      CARD_WIDTH,
      CARD_HEIGHT,
      CARD_RADIUS,
    );
    container.add(bg);

    const dot = this.scene.add.circle(-CARD_WIDTH / 2 + 22, 0, 9, accentColor, 1);
    dot.setStrokeStyle(1.5, 0xffffff, 1);
    container.add(dot);

    const titleText = this.scene.add
      .text(-CARD_WIDTH / 2 + 40, -CARD_HEIGHT / 2 + 8, title, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    container.add(titleText);

    const bodyText = this.scene.add
      .text(-CARD_WIDTH / 2 + 40, -CARD_HEIGHT / 2 + 26, body, {
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '11px',
        color: '#444444',
        wordWrap: { width: CARD_WIDTH - 60 },
        lineSpacing: 2,
      })
      .setOrigin(0, 0);
    container.add(bodyText);

    container.setAlpha(0).setScale(0.88).setY(TOOLTIP_Y + 8);
    this.activeTween = this.scene.tweens.add({
      targets: container,
      alpha: 1,
      scale: 1,
      y: TOOLTIP_Y,
      duration: BALANCE.ui.tooltipFadeInMs,
      ease: 'Back.easeOut',
    });

    const owner = container;
    this.scene.time.delayedCall(
      BALANCE.ui.tooltipFadeInMs + BALANCE.ui.tooltipDurationMs,
      () => {
        if (!owner.scene || this.active !== owner) return;
        this.activeTween = this.scene.tweens.add({
          targets: owner,
          alpha: 0,
          y: TOOLTIP_Y - 10,
          duration: BALANCE.ui.tooltipFadeOutMs,
          ease: 'Quad.easeIn',
          onComplete: () => {
            owner.destroy();
            if (this.active === owner) this.active = undefined;
          },
        });
      },
    );
  }
}
